import { memo } from "react";
import { Box, TextField } from "@mui/material";

// ------------------- Shared Styles -------------------
const baseText = {
  fontSize: "14px",
  fontFamily: `"Montserrat", Verdana, sans-serif`,
} as const;

export const fieldRowSx = {
  position: "relative" as const,
  mx: "auto",
  overflow: "visible",
};

export const noticeTextSx = {
  fontSize: "16px",
  fontFamily: `"Montserrat", Verdana, sans-serif`,
  color: "#494440",
  fontWeight: 400,
  lineHeight: 1.6,
} as const;

export const linkSx = {
  ...baseText,
  color: "#62b5e5",
  fontWeight: 700,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

export const errorTextSx = {
  ...baseText,
  color: "#af272f",
  mt: 0.5,
} as const;

// ------------------- Component Types -------------------
export type BorderedInputProps = {
  name: string;
  value: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  type?: string;
  readOnly?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  helperText?: string;
  error?: boolean;
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
};

// ------------------- Component -------------------
const BorderedInputLansing = memo(function BorderedInputLansing({
  name,
  value,
  onChange,
  required,
  type = "text",
  inputProps,
  readOnly,
  onKeyDown,
  onPaste,
  helperText,
  error,
  inputMode,
}: BorderedInputProps) {
  return (
    <Box>
      <Box
        sx={{
          borderWidth: 1,
          borderColor: error ? "#af272f" : "#d9d9d9",
          borderStyle: "solid",
          borderRadius: "4px",
          width: "100%",
        }}
      >
        <TextField
          variant="standard"
          fullWidth
          name={name}
          type={type}
          value={value}
          onChange={onChange ?? (() => {})}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          helperText={helperText}
          error={error}
          FormHelperTextProps={{
            sx: {
              m: 0,
              p: 0,
              ...(error && helperText ? errorTextSx : {}),
              ...(!helperText && {
                height: 0,
                marginTop: 0,
                marginBottom: 0,
                overflow: "hidden",
                visibility: "hidden",
                fontSize: 0,
                lineHeight: 0,
              }),
              ...(helperText && {
                marginTop: "4px",
                fontSize: "11px",
                lineHeight: 1.43,
                visibility: "visible",
                height: "auto",
                position: "absolute",
                top: "28px",
              }),
            },
          }}
          InputProps={{
            readOnly: readOnly,
            disableUnderline: true,
            sx: {
              ...baseText,
              color: "#000",
              height: "32px",
              px: "10px",
              "& input": {
                fontSize: "14px",
              },
            },
          }}
          inputProps={{
            ...inputProps,
            ...(inputMode && { inputMode: inputMode }),
          }}
        />
      </Box>
    </Box>
  );
});

export default BorderedInputLansing;
